import React, { useState } from 'react';
import { supabase } from '../supabaseClient';
import { Mail, Lock, ShieldCheck, Loader, Smartphone } from 'lucide-react';

const Login = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // App States: 'login' -> 'setup' (QR Code) -> 'verify' (Enter Code)
  const [step, setStep] = useState('login');
  
  // Standard Form State
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  // 2FA State
  const [factorId, setFactorId] = useState(null);
  const [qrCode, setQrCode] = useState(null);
  const [authCode, setAuthCode] = useState('');

  // --- STEP 1: INITIAL PASSWORD LOGIN (WITH DEBUGGING) ---
  const handleSignIn = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      console.log("1. Starting password check...");
      const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      
      if (authError) throw authError;
      console.log("2. Password correct! Fetching global security settings...");

      // Fetch global settings
      const { data: settings, error: settingsError } = await supabase
        .from('crm_settings')
        .select('enforce_2fa')
        .eq('id', 'global')
        .maybeSingle();

      if (settingsError) console.error("Warning: Could not read settings table:", settingsError);

      const enforce2FA = settings?.enforce_2fa || false;
      console.log(`3. Is 2FA enforced in DB? => ${enforce2FA}`);

      // If 2FA is enforced, check the user's status
      if (enforce2FA) {
        console.log("4. 2FA is ON. Checking if user has an Authenticator linked...");
        const { data: factors, error: factorsError } = await supabase.auth.mfa.listFactors();
        if (factorsError) throw factorsError;

        const activeFactor = factors.totp[0];

        if (!activeFactor) {
          console.log("5. User needs setup! Generating QR code...");
          const { data: enrollData, error: enrollError } = await supabase.auth.mfa.enroll({
            factorType: 'totp'
          });
          
          if (enrollError) {
             console.error("MFA ENROLL ERROR:", enrollError);
             throw new Error("Supabase blocked the QR code. Is TOTP MFA enabled in your Supabase Dashboard?");
          }

          setFactorId(enrollData.id);
          setQrCode(enrollData.totp.qr_code); 
          setStep('setup');
          setLoading(false);
          return;
        } else {
          console.log("5. User already set up. Asking for 6-digit code...");
          setFactorId(activeFactor.id);
          setStep('verify');
          setLoading(false);
          return;
        }
      }

      console.log("4. 2FA is OFF. Letting user directly into the CRM...");
      await finalizeLogin(authData.user.id);
      
      // Force the App router to re-evaluate the session and let us in
      window.location.reload();

    } catch (err) {
      console.error("LOGIN CRASH:", err);
      // Show the actual error on the screen instead of a generic message!
      setError(err.message || "An unexpected error occurred.");
    } finally {
      if (step === 'login') setLoading(false);
    }
  };

  // --- STEP 2: VERIFY THE 6-DIGIT CODE ---
  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Send the code to Supabase to upgrade the session to AAL2 (Highly Secure)
      const { data, error: verifyError } = await supabase.auth.mfa.challengeAndVerify({
        factorId: factorId,
        code: authCode
      });

      if (verifyError) throw verifyError;

      // Success! Log them in.
      const { data: { user } } = await supabase.auth.getUser();
      await finalizeLogin(user.id);

    } catch (err) {
      console.error(err);
      setError("Invalid security code. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // --- FINAL ROUTING / ONLINE STATUS ---
  const finalizeLogin = async (userId) => {
    try {
      await supabase
        .from('profiles')
        .update({ status: 'online' })
        .eq('id', userId);
        
      // Your App.jsx router will automatically detect the secure session and load the CRM!
    } catch (err) {
      console.error("Failed to update presence:", err);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4 selection:bg-amber-500/30">
      <div className="w-full max-w-md bg-slate-900 border border-slate-800 rounded-2xl p-8 shadow-2xl animate-fade-in">
        
        {/* HEADER ICON */}
        <div className="flex justify-center mb-6">
          <div className="w-16 h-16 bg-blue-600/20 text-blue-500 rounded-full flex items-center justify-center border border-blue-500/30 shadow-[0_0_15px_rgba(59,130,246,0.2)]">
            {step === 'login' ? <ShieldCheck size={32} /> : <Smartphone size={32} className="animate-pulse" />}
          </div>
        </div>
        
        <h2 className="text-2xl font-bold text-white text-center mb-2">
          {step === 'login' ? 'Knowmia Floor Login' : 'Two-Factor Authentication'}
        </h2>
        <p className="text-slate-400 text-sm text-center mb-8">
          {step === 'login' 
            ? 'Enter your secure credentials to access the floor.' 
            : step === 'setup' 
            ? 'Scan this QR code with Google Authenticator or Authy to secure your account.'
            : 'Enter the 6-digit code from your Authenticator app.'}
        </p>

        {error && (
          <div className="bg-red-500/10 border border-red-500/50 text-red-400 p-3 rounded-lg text-sm mb-6 text-center animate-fade-in">
            {error}
          </div>
        )}

        {/* --- FORM 1: STANDARD LOGIN --- */}
        {step === 'login' && (
          <form onSubmit={handleSignIn} className="space-y-4">
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
              <input 
                type="email" 
                required 
                placeholder="Work Email" 
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-slate-950 border border-slate-800 rounded-lg py-3 pl-10 pr-4 text-white placeholder-slate-500 focus:border-blue-500 outline-none transition-colors"
              />
            </div>

            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
              <input 
                type="password" 
                required 
                placeholder="Password" 
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-slate-950 border border-slate-800 rounded-lg py-3 pl-10 pr-4 text-white placeholder-slate-500 focus:border-blue-500 outline-none transition-colors"
              />
            </div>

            <button 
              type="submit" 
              disabled={loading}
              className="w-full bg-blue-600 hover:bg-blue-500 text-white font-bold py-3 rounded-lg transition-colors flex items-center justify-center gap-2 mt-6"
            >
              {loading ? <Loader className="animate-spin" size={18} /> : 'Secure Sign In'}
            </button>
          </form>
        )}

        {/* --- FORM 2: 2FA SETUP & VERIFICATION --- */}
        {(step === 'setup' || step === 'verify') && (
          <form onSubmit={handleVerifyCode} className="space-y-6 animate-slide-in-right">
            
            {/* Show the QR Code if it's their first time logging in with 2FA enforced */}
            {step === 'setup' && qrCode && (
              <div className="flex justify-center items-center bg-white p-4 rounded-xl w-[200px] h-[200px] mx-auto border-4 border-slate-800">
                <img 
                  src={qrCode} 
                  alt="2FA QR Code" 
                  className="w-full h-full object-contain"
                />
              </div>
            )}

            <div>
              <label className="text-xs font-bold text-slate-400 uppercase tracking-wide block mb-2 text-center">
                Authentication Code
              </label>
              <input 
                type="text" 
                required 
                maxLength="6"
                placeholder="000000" 
                value={authCode}
                onChange={(e) => setAuthCode(e.target.value.replace(/\D/g, ''))}
                className="w-full bg-slate-950 border border-slate-800 rounded-lg py-4 px-4 text-white text-center text-2xl tracking-[0.5em] font-mono focus:border-blue-500 outline-none transition-colors"
              />
            </div>

            <button 
              type="submit" 
              disabled={loading || authCode.length !== 6}
              className="w-full bg-amber-500 hover:bg-amber-400 text-slate-900 disabled:opacity-50 disabled:cursor-not-allowed font-bold py-3 rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              {loading ? <Loader className="animate-spin text-slate-900" size={18} /> : 'Verify Identity'}
            </button>
            
            <button 
              type="button"
              onClick={() => { setStep('login'); setAuthCode(''); setPassword(''); }}
              className="w-full text-slate-500 hover:text-white text-sm transition-colors"
            >
              Cancel and Return to Login
            </button>
          </form>
        )}

      </div>
    </div>
  );
};

export default Login;